import { useState, useEffect, useCallback } from "react";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ClipboardList,
  RefreshCw,
  LogIn,
  LogOut,
  FileSpreadsheet,
  Mail,
  User,
  Globe,
  ChevronLeft,
  ChevronRight,
  Clock,
  Timer,
} from "lucide-react";
import { api } from "../lib/utils";

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionFilter, setActionFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [page, setPage] = useState(0);
  const limit = 20;

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get(`/api/audit-logs/users`);
      setUsers(res.data || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append("limit", limit.toString());
      params.append("skip", (page * limit).toString());
      if (actionFilter !== "all") params.append("action", actionFilter);
      if (userFilter !== "all") params.append("user_id", userFilter);

      const res = await api.get(`/api/audit-logs?${params}`);
      setLogs(res.data);
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
    }
  }, [page, actionFilter, userFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get(`/api/audit-logs/stats`);
      setStats(res.data);
    } catch (error) {
      console.error("Failed to fetch audit stats:", error);
    }
  }, []);

  const fetchAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      await Promise.all([fetchLogs(), fetchStats(), fetchUsers()]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchLogs, fetchStats, fetchUsers]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    fetchLogs();
  }, [page, actionFilter, userFilter, fetchLogs]);

  const formatDateTime = (isoString) => {
    if (!isoString) return "---";
    const date = new Date(isoString);
    return date.toLocaleString("en-MY", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZone: "Asia/Kuala_Lumpur",
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return "0s";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "login":
        return <LogIn className="h-4 w-4 text-green-500" />;
      case "logout":
        return <LogOut className="h-4 w-4 text-orange-500" />;
      case "excel_export":
        return <FileSpreadsheet className="h-4 w-4 text-blue-500" />;
      case "email_sent":
        return <Mail className="h-4 w-4 text-purple-500" />;
      default:
        return <ClipboardList className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getActionBadge = (action) => {
    const styles = {
      login: "bg-green-500/10 text-green-500 border-green-500/20",
      logout: "bg-orange-500/10 text-orange-500 border-orange-500/20",
      excel_export: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      email_sent: "bg-purple-500/10 text-purple-500 border-purple-500/20",
    };
    return (
      <Badge variant="outline" className={styles[action] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/20"}>
        {action?.replace("_", " ").toUpperCase() || "UNKNOWN"}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto p-6 md:p-8" data-testid="audit-logs-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">
              AUDIT LOGS
            </h1>
            <p className="text-muted-foreground mt-1">
              Track user activity and system events
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchAll(true)}
            disabled={refreshing}
            data-testid="refresh-audit-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <Card className="bg-card/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <LogIn className="h-5 w-5 text-green-500" />
                <span className="text-2xl font-bold">{stats?.total_logins || 0}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Total Logins</p>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <FileSpreadsheet className="h-5 w-5 text-blue-500" />
                <span className="text-2xl font-bold">{stats?.total_exports || 0}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Excel Exports</p>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Mail className="h-5 w-5 text-purple-500" />
                <span className="text-2xl font-bold">{stats?.total_emails_sent || 0}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Emails Sent</p>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <User className="h-5 w-5 text-cyan-500" />
                <span className="text-2xl font-bold">{stats?.unique_users || 0}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Active Users</p>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Clock className="h-5 w-5 text-yellow-500" />
                <span className="text-2xl font-bold">{stats?.session_stats?.total_sessions || 0}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Sessions</p>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Timer className="h-5 w-5 text-orange-500" />
                <span className="text-lg font-bold">{formatDuration(stats?.session_stats?.avg_duration_seconds)}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Avg Session</p>
            </CardContent>
          </Card>
        </div>

        {/* Three Column: Top IPs, Top Users, Session Duration */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-card/50 border-border">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Globe className="h-5 w-5" />
                Top Login IPs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats?.login_by_ip?.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No data</p>
                ) : (
                  stats?.login_by_ip?.slice(0, 5).map((item, idx) => (
                    <div key={item.ip} className="flex items-center justify-between p-2 rounded bg-muted/30">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">{idx + 1}.</span>
                        <span className="font-mono text-sm">{item.ip}</span>
                      </div>
                      <Badge variant="outline">{item.count}</Badge>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-5 w-5" />
                Most Active Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats?.activity_by_user?.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No data</p>
                ) : (
                  stats?.activity_by_user?.slice(0, 5).map((item, idx) => (
                    <div key={item.user_id} className="flex items-center justify-between p-2 rounded bg-muted/30">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">{idx + 1}.</span>
                        <span className="text-sm truncate max-w-[120px]">{item.email}</span>
                      </div>
                      <Badge variant="outline">{item.actions} actions</Badge>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Timer className="h-5 w-5" />
                Session Time by User
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats?.session_by_user?.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No data</p>
                ) : (
                  stats?.session_by_user?.slice(0, 5).map((item, idx) => (
                    <div key={item.user_id} className="flex items-center justify-between p-2 rounded bg-muted/30">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">{idx + 1}.</span>
                        <span className="text-sm truncate max-w-[100px]">{item.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{item.session_count} sess</Badge>
                        <Badge variant="secondary" className="text-xs">{formatDuration(item.total_time_seconds)}</Badge>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Audit Log Table */}
        <Card className="bg-card/50 border-border">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <ClipboardList className="h-5 w-5" />
                Activity Log
              </CardTitle>
              <div className="flex items-center gap-3">
                <Select value={userFilter} onValueChange={(v) => { setUserFilter(v); setPage(0); }}>
                  <SelectTrigger className="w-[200px]" data-testid="user-filter">
                    <SelectValue placeholder="Filter by user" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
                  <SelectTrigger className="w-[160px]" data-testid="action-filter">
                    <SelectValue placeholder="Filter by action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    <SelectItem value="login">Logins</SelectItem>
                    <SelectItem value="logout">Logouts</SelectItem>
                    <SelectItem value="excel_export">Excel Exports</SelectItem>
                    <SelectItem value="email_sent">Emails Sent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="w-full">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground font-medium">Time</TableHead>
                    <TableHead className="text-muted-foreground font-medium">User</TableHead>
                    <TableHead className="text-muted-foreground font-medium">Action</TableHead>
                    <TableHead className="text-muted-foreground font-medium">IP Address</TableHead>
                    <TableHead className="text-muted-foreground font-medium">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-12">
                        <ClipboardList className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-muted-foreground">No audit logs found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id} className="border-border">
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDateTime(log.timestamp)}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm truncate max-w-[200px] block">{log.user_email}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getActionIcon(log.action)}
                            {getActionBadge(log.action)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm">{log.ip_address || "---"}</span>
                        </TableCell>
                        <TableCell>
                          {log.details && Object.keys(log.details).length > 0 ? (
                            <span className="text-xs text-muted-foreground">
                              {log.action === "excel_export" && `${log.details.record_count} records`}
                              {log.action === "email_sent" && `${log.details.recipients} recipients`}
                              {log.action === "login" && log.details.method}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">---</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>

            {/* Pagination */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Showing {page * limit + 1} to {page * limit + logs.length} results
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  Page {page + 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={logs.length < limit}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
